<template>
  <BOverlay
    :show="pending"
    opacity="0.5"
    spinner-variant="primary"
  >
    <div class="reconciliation-form">
      <BTabs v-model="activeTab">
        <BTab
          :title="$t('activities.resources.production')"
        >
          <div
            v-if="!readOnly"
            class="d-flex align-items-center my-4"
          >
            <ProductionReconciliationFormInputFieldsInfo />

            <div class="ml-5">
              <BButton
                size="sm"
                variant="outline-secondary"
                class="text-nowrap"
                @click="requestEntries"
              >
                <i class="fas fa-refresh mr-1" />
                {{ $t('activities.productionReconciliation.lastRefresh') }}:
                {{ lastRefreshDateText }}
              </BButton>
            </div>
          </div>

          <ProductionReconciliationTable
            v-show="activeTab === 0"
            :key="lastRefreshDate"
            :value-column-label="$t('activities.resources.production')"
            :entries="productionEntries"
            :unit="orderUnit"
            :counter-value="Number(orderDone) || 0"
            :new-entry-object.sync="newEntryObject"
            :order-start-date="orderBeginDate"
            :read-only="readOnly"
            @enter-pressed="addEntry()"
            @delete-entry="deleteEntry($event)"
          />

          <p
            v-if="differentUnitOnAnyEntry"
            class="small text-danger"
          >
            <i class="fas fa-exclamation-circle mr-1" />
            {{ $t('activities.productionReconciliation.entryWithDifferentUnitError') }}
            ({{ $t('activities.productionReconciliation.orderUnit') }}:
            <span class="font-weight-bold">{{ orderUnit }}</span>)
          </p>

          <div
            v-if="!readOnly"
            class="d-flex justify-content-center mt-4"
          >
            <BButton
              size="sm"
              class="mr-5 w-25"
              @click="hideModal"
            >
              {{ $t('general.cancel') }}
            </BButton>
            <BButton
              size="sm"
              class="w-25"
              variant="primary"
              :disabled="!isSaveEnabled"
              @click="addEntry()"
            >
              <i class="fas fa-check mr-1" />
              {{ $t('activities.productionReconciliation.saveEntry') }}
            </BButton>
          </div>
        </BTab>
        <BTab
          :title="$t('activities.resources.waste')"
        >
          <div
            v-if="!readOnly"
            class="d-flex align-items-center my-4"
          >
            <ProductionReconciliationFormInputFieldsInfo />

            <div class="ml-5">
              <BButton
                size="sm"
                variant="outline-secondary"
                class="text-nowrap"
              >
                <i class="fas fa-refresh mr-1" />
                {{ $t('activities.productionReconciliation.lastRefresh') }}:
                {{ lastRefreshDateText }}
              </BButton>
            </div>
          </div>

          <ProductionReconciliationTable
            v-show="activeTab === 1"
            :key="lastRefreshDate"
            :value-column-label="$t('activities.resources.waste')"
            :entries="wasteEntries"
            display-as-waste
            :unit="orderUnit"
            :counter-value="Number(orderWaste) || 0"
            :new-entry-object.sync="newEntryObject"
            :read-only="readOnly"
            :order-start-date="orderBeginDate"
            @delete-entry="deleteEntry($event)"
            @enter-pressed="addEntry()"
          />

          <div
            v-if="!readOnly"
            class="d-flex justify-content-center mt-4"
          >
            <BButton
              size="sm"
              class="mr-5 w-25"
              @click="hideModal"
            >
              {{ $t('general.cancel') }}
            </BButton>
            <BButton
              size="sm"
              class="w-25"
              variant="primary"
              :disabled="!isSaveEnabled"
              @click="addEntry()"
            >
              <i class="fas fa-check mr-1" />
              {{ $t('activities.productionReconciliation.saveEntry') }}
            </BButton>
          </div>
        </BTab>
      </BTabs>
    </div>
  </BOverlay>
</template>

<script>
/* eslint-disable max-len */
import ProductionReconciliationFormInputFieldsInfo from '@/components/activity/productionReconciliation/ProductionReconciliationFormInputFieldsInfo';
import ProductionReconciliationTable from '@/components/activity/productionReconciliation/ProductionReconciliationTable';
/* eslint-enable max-len */
import loopRequest from '@/mixins/loopRequest';
import { activityResourceType } from '@/store/modules/activities';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'ProductionReconciliationForm',
  props: {
    activityId: String,
    orderBeginDate: String,
    orderLineId: String,
    orderUnit: String,
    orderDone: Number,
    orderWaste: Number,
    readOnly: Boolean,
  },
  data: () => ({
    activeTab: 0,

    pending: false,
    entries: [],
    lastRefreshDate: null,
    lastRefreshDateText: '',

    newEntryObject: {
      to: '',
      value: 0,
      resourceType: '',
    },
  }),
  components: {
    ProductionReconciliationTable,
    ProductionReconciliationFormInputFieldsInfo,
  },
  mixins: [
    loopRequest('updateLastRefreshText', 1000),
  ],
  computed: {
    ...mapGetters([
      'plantId',
    ]),
    showEntryValueError() {
      return this.newEntryObject.value
        && (this.newEntryObject.value < 0 || this.newEntryObject.value > 1000000000);
    },
    differentUnitOnAnyEntry() {
      return this.entries.some(e => e.unit !== this.orderUnit);
    },
    productionEntries() {
      return this.entries.filter(e => e.type === activityResourceType.production);
    },
    wasteEntries() {
      return this.entries.filter(e => e.type === activityResourceType.waste);
    },
    isSaveEnabled() {
      return this.newEntryObject.value
        && this.newEntryObject.to
        && moment(this.newEntryObject.to).unix() > moment(this.orderBeginDate).unix()
        && this.newEntryObject.resourceType
        && !this.differentUnitOnAnyEntry
        && !this.showEntryValueError;
    },
  },
  methods: {
    ...mapActions([
      'getActivityManualResources',
      'addOrderManualProduction',
      'removeOrderManualProduction',
    ]),
    updateLastRefreshText() {
      if (!this.lastRefreshDate) return;
      this.lastRefreshDateText = moment.unix(this.lastRefreshDate).fromNow();
    },
    async requestEntries() {
      try {
        this.pending = true;
        const { data } = await this.getActivityManualResources({
          params: {
            plantId: this.plantId,
            activityId: this.activityId,
          },
        });
        this.entries = data;
        this.lastRefreshDate = moment().unix();
        this.updateLastRefreshText();
      } catch ({ response }) {
        this.$root.$bvToast
          .toast(response.data?.detail || JSON.stringify(response.data), {
            title: this.$t('error.error'),
            variant: 'danger',
            appendToast: true,
            toaster: 'b-toaster-top-right',
          });
      } finally {
        this.pending = false;
      }
    },
    async addEntry() {
      if (!this.isSaveEnabled) return;

      try {
        this.pending = true;
        await this.addOrderManualProduction({
          params: {
            plantId: this.plantId,
            orderId: this.activityId,
          },
          data: {
            value: this.newEntryObject.value,
            resourceType: this.newEntryObject.resourceType,
            to: this.newEntryObject.to,
            incremental: true,
            unit: this.orderUnit,
          },
        });
        await this.requestEntries();
      } catch ({ response }) {
        this.$root.$bvToast
          .toast(response.data?.detail || JSON.stringify(response.data), {
            title: this.$t('error.error'),
            variant: 'danger',
            appendToast: true,
            toaster: 'b-toaster-top-right',
          });
      } finally {
        this.pending = false;
      }
    },
    async deleteEntry(entry) {
      try {
        this.pending = true;
        await this.removeOrderManualProduction({
          params: {
            plantId: this.plantId,
            orderId: this.activityId,
            query: {
              to: entry.to,
              resourceType: entry.type,
              lineId: this.orderLineId,
              activityBeginDate: this.orderBeginDate,
            },
          },
        });
        await this.requestEntries();
      } catch ({ response }) {
        this.$root.$bvToast
          .toast(response.data?.detail || JSON.stringify(response.data), {
            title: this.$t('error.error'),
            variant: 'danger',
            appendToast: true,
            toaster: 'b-toaster-top-right',
          });
      } finally {
        this.pending = false;
      }
    },
    hideModal() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          productionReconciliation: false,
        },
      });
    },
  },
  created() {
    this.requestEntries();
  },
};
</script>

<style scoped lang="scss">
.reconciliation-form {
  &:deep() {
    .nav-tabs {
      .nav-item {
        width: 25%;
        font-size: 14px;
        text-align: center;

        .nav-link {
          color: gray;
          margin-bottom: -3px;
          transition: 0.2s all;
          border-top: unset;
          border-left: unset;
          border-right: unset;

          &.active, &:hover {
            color: black;
            border-bottom: 5px solid var(--blue);
            border-radius: 0;
            border-top: unset;
            border-left: unset;
            border-right: unset;
            font-weight: bold;
          }

        }
      }
    }
  }
}
</style>
